import { AnchorContainer, AnchorTags, ChildrenContainer, Container, Icon } from './AnchorList.styles';
import { CSS_SELECTOR, useCustomCss } from '../../contexts/CustomCss';
import { ComponentChild, FunctionalComponent, h } from 'preact';
import { ANALYTICS_TYPE } from '../../enums';
import { Icon as CustomIcon } from '../CustomizeAnchor/CustomizeAnchor.styles';
import { LinkIcon } from '../../assets';
import { dispatchAnalyticsEvent } from '../../modules/helpers';
import { memo } from 'preact/compat';
import { useUiContext } from '../../contexts/UiContext';

function toHrefUrl(url: string, baseHref = ''): string {
  if (baseHref) {
    if (!baseHref.endsWith('/') && !url.startsWith('/')) {
      return `${baseHref}/${url}`;
    }

    if (baseHref.endsWith('/') && url.startsWith('/')) {
      return `${baseHref.slice(0, -1)}${url}`;
    }

    return `${baseHref}${url}`;
  }

  const sanitizedUrl = url.includes('//') || url.startsWith('/') || url.startsWith('#') ? url : `//${url}`;

  return sanitizedUrl;
}

type Link = {
  ariaLabel: string;
  label: string | null;
  type: ANALYTICS_TYPE;
  url: string | null;
};

type Button = {
  icon?: ComponentChild;
  label?: string;
  onClick?: () => void;
};

type Props = {
  buttons?: Button[];
  links: Link[];
  withoutMargin: boolean;
};

const AnchorList: FunctionalComponent<Props> = memo(({ buttons = [], children, links, withoutMargin = false }) => {
  const { abTestVariant, baseHref, view } = useUiContext();
  const { rules } = useCustomCss(CSS_SELECTOR.LINKS);

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const onLinkClick = (event: any, linkUrl: string | null | undefined, type: ANALYTICS_TYPE): void => {
    const linkMatch = linkUrl && linkUrl.match(/^#onUc(.*)Click$/);
    dispatchAnalyticsEvent(view, type, abTestVariant);

    if (linkMatch && linkMatch.length) {
      event.preventDefault();

      const customClickEvent = new window.CustomEvent(linkMatch[0].substr(1));
      window.dispatchEvent(customClickEvent);
    }
  };

  const filteredLinks = links.filter((link) => link.url && link.label);
  const filteredButtons = buttons.filter((button) => button.onClick && button.label);

  if (!filteredLinks.length && !filteredButtons.length && !children) {
    return null;
  }

  return (
    <Container withoutMargin={withoutMargin}>
      <AnchorContainer data-testid="uc-anchors">
        {filteredLinks.map((link: Link) => (
          <AnchorTags
            aria-label={link.ariaLabel}
            data-testid="uc-anchor-link"
            href={toHrefUrl(link.url || '', baseHref)}
            onClick={(event) => onLinkClick(event, link.url, link.type)}
            target="_blank"
            rel="noopener"
            style={{ ...rules }}
          >
            <Icon>
              <LinkIcon />
            </Icon>
            {link.label}
          </AnchorTags>
        ))}
        {filteredButtons.map((button: Button) => (
          <AnchorTags
            data-testid="uc-anchor-button"
            onClick={button.onClick}
            target="_blank"
            rel="noopener"
            style={{ ...rules }}
          >
            <CustomIcon>{button.icon}</CustomIcon>
            {button.label}
          </AnchorTags>
        ))}
      </AnchorContainer>
      <ChildrenContainer>{children}</ChildrenContainer>
    </Container>
  );
});

export default AnchorList;
